import { Badge, Box, Button, Divider, Group, Paper, rem, Select, Stack, Text, ThemeIcon, Tooltip, Transition } from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { openConfirmModal } from "@mantine/modals";
import { IconArrowBackUp, IconCheck, IconChecks, IconUsersGroup, IconX } from "@tabler/icons-react";
import { useState } from "react";
import type { FeePolicy } from "../../apis/tenantFeePolicy";

interface Props {
  selectedCount: number;
  namedPolicies: FeePolicy[];
  /** Called with feePolicyId=null when user picks "Revert to DEFAULT". */
  onApply: (params: { feePolicyId: string | null; effectiveFrom: string }) => Promise<void>;
  onCancel: () => void;
}

const REVERT_VALUE = "__REVERT__";

export function BulkAssignBar({ selectedCount, namedPolicies, onApply, onCancel }: Props) {
  const [policyValue, setPolicyValue] = useState<string | null>(null);
  const [effectiveFrom, setEffectiveFrom] = useState<Date | null>(new Date());
  const [busy, setBusy] = useState(false);

  const rateLabel = (p: FeePolicy): string => {
    if (p.calculationMethod === "PERCENTAGE" && p.feeRate) return `${(Number(p.feeRate) * 100).toFixed(2)}%`;
    if (p.calculationMethod === "FLAT") return "Тогтмол шимтгэл";
    return "";
  };

  // Plain label + secondary description per option, looked up by value when
  // rendering the dropdown and when summarising the choice in the confirm modal.
  const optionMeta: Record<string, { label: string; description?: string }> = {
    [REVERT_VALUE]: {
      label      : "Стандарт (DEFAULT) бодлого",
      description: "Тусгай оноолтыг цуцалж, стандарт шимтгэлд буцаана",
    },
  };
  for (const p of namedPolicies) {
    optionMeta[p.id] = { label: p.name ?? "(нэргүй)", description: rateLabel(p) || undefined };
  }

  // Grouped so the standard policy reads as a distinct choice, not an action
  // jumbled in with the named policies.
  const data = [
    ...(namedPolicies.length
      ? [{
        group: "Нэрлэсэн бодлого",
        items: namedPolicies.map((p) => ({ value: p.id, label: optionMeta[p.id].label })),
      }]
      : []),
    {
      group: "Стандарт",
      items: [{ value: REVERT_VALUE, label: optionMeta[REVERT_VALUE].label }],
    },
  ];

  const canApply = selectedCount > 0 && policyValue !== null && effectiveFrom !== null && !busy;
  const isRevert = policyValue === REVERT_VALUE;

  const runApply = async (iso: string) => {
    setBusy(true);
    try {
      await onApply({
        feePolicyId: policyValue === REVERT_VALUE ? null : policyValue,
        effectiveFrom: iso,
      });
    } finally {
      setBusy(false);
    }
  };

  const handleApply = () => {
    if (!policyValue || !effectiveFrom) return;
    const iso = effectiveFrom.toISOString().slice(0, 10);
    const policyLabel = optionMeta[policyValue]?.label ?? policyValue;
    const accent = isRevert ? "orange" : "teal";

    openConfirmModal({
      centered: true,
      radius: "md",
      size: "md",
      padding: "lg",
      withCloseButton: false,
      children: (
        <Stack gap="md">
          <Group gap="sm" wrap="nowrap" align="flex-start">
            <ThemeIcon variant="light" color={accent} radius="xl" size={48}>
              {isRevert ? <IconArrowBackUp size={26} /> : <IconChecks size={26} />}
            </ThemeIcon>
            <Box>
              <Text fw={700} size="lg">
                {isRevert ? "DEFAULT бодлогод буцаах" : "Бодлого оноох"}
              </Text>
              <Text size="sm" c="dimmed">
                {isRevert
                  ? "Сонгосон харилцагчид DEFAULT шимтгэлийн бодлогод шилжинэ."
                  : "Сонгосон харилцагчдад дараах бодлогыг онооно."}
              </Text>
            </Box>
          </Group>

          <Paper withBorder radius="md" p="sm" bg="gray.0">
            <Stack gap="xs">
              <Group justify="space-between" gap="xl" wrap="nowrap">
                <Text size="sm" c="dimmed">Бодлого</Text>
                <Text size="sm" fw={600} ta="right">{policyLabel}</Text>
              </Group>
              <Divider />
              <Group justify="space-between" wrap="nowrap">
                <Text size="sm" c="dimmed">Харилцагч</Text>
                <Badge color={accent} variant="light" radius="sm">{selectedCount}</Badge>
              </Group>
              <Divider />
              <Group justify="space-between" wrap="nowrap">
                <Text size="sm" c="dimmed">Эхлэх огноо</Text>
                <Text size="sm" fw={600} style={{ fontVariantNumeric: "tabular-nums" }}>{iso}</Text>
              </Group>
            </Stack>
          </Paper>
        </Stack>
      ),
      labels: { confirm: isRevert ? "Буцаах" : "Оноох", cancel: "Болих" },
      confirmProps: { color: accent, leftSection: isRevert ? <IconArrowBackUp size={16} /> : <IconCheck size={16} /> },
      onConfirm: () => void runApply(iso),
    });
  };

  const noPolicy = policyValue === null;

  return (
    <Transition mounted={selectedCount > 0} transition="slide-down" duration={180}>
      {(styles) => (
        <Paper p="md" withBorder radius="md" bg="brand.0" style={styles}>
          <Group justify="space-between" wrap="wrap" gap="lg" align="flex-end">
            <Group gap="lg" wrap="wrap" align="flex-end">
              <Group gap="sm" wrap="nowrap" pb={4}>
                <ThemeIcon variant="light" color="brand" radius="xl" size={40}>
                  <IconUsersGroup size={22} />
                </ThemeIcon>
                <Box>
                  <Text size="xs" c="dimmed" fw={500}>Сонгогдсон</Text>
                  <Group gap={6} align="baseline">
                    <Badge color="brand" variant="filled" size="lg" radius="sm">
                      {selectedCount}
                    </Badge>
                    <Text size="sm" c="dimmed">харилцагч</Text>
                  </Group>
                </Box>
              </Group>

              <Divider orientation="vertical" style={{ height: rem(40), alignSelf: "center" }} />

              <Select
                label="Оноох бодлого"
                placeholder="Бодлого сонгох"
                data={data}
                value={policyValue}
                onChange={setPolicyValue}
                w={300}
                searchable
                withAsterisk
                renderOption={({ option, checked }) => {
                  const meta = optionMeta[option.value];
                  return (
                    <Group gap="sm" wrap="nowrap" justify="space-between" w="100%">
                      <Box>
                        <Text size="sm">{meta?.label ?? option.label}</Text>
                        {meta?.description && (
                          <Text size="xs" c="dimmed">{meta.description}</Text>
                        )}
                      </Box>
                      {checked && <IconCheck size={16} />}
                    </Group>
                  );
                }}
              />
              <DateInput
                label="Эхлэх огноо"
                value={effectiveFrom}
                onChange={(v: unknown) => setEffectiveFrom(typeof v === "string" ? new Date(v) : (v as Date | null))}
                w={160}
                valueFormat="YYYY-MM-DD"
              />
            </Group>

            <Group gap="xs" pb={4}>
              <Button
                variant="subtle"
                color="gray"
                leftSection={<IconX size={16} />}
                onClick={onCancel}
                disabled={busy}
              >
                Цуцлах
              </Button>
              <Tooltip label="Эхлээд бодлого сонгоно уу" disabled={!noPolicy} withArrow position="top">
                <Button
                  color={isRevert ? "orange" : undefined}
                  leftSection={isRevert ? <IconArrowBackUp size={16} /> : <IconCheck size={16} />}
                  onClick={(e) => {
                    if (!canApply) {
                      e.preventDefault();
                      return;
                    }
                    handleApply();
                  }}
                  loading={busy}
                  data-disabled={!canApply || undefined}
                >
                  {isRevert ? "Буцаах" : "Оноох"}
                </Button>
              </Tooltip>
            </Group>
          </Group>
        </Paper>
      )}
    </Transition>
  );
}
